/* Messaging Centre — bulk SMS broadcasts.
   --------------------------------------------------------
   Lets super_admin / entity_admin / back_office segment subscribers by
   category, campus and performance bucket, write a message (with live
   SMS-count metering) and fan-out a broadcast through the same provider
   that powers single-subscriber SMS.
*/
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChatCircleText,
  PaperPlaneTilt,
  Users,
  GraduationCap,
  Tag,
  Pulse,
  Spinner,
  WarningCircle,
  CaretDown,
  Check,
} from '@phosphor-icons/react';

import { useAuth } from '../contexts/AuthContext';

import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES_FALLBACK = ['STUDENT', 'STAFF', 'FACULTY', 'VISITOR', 'OTHER'];
const STATUS_OPTIONS = ['Excellent', 'Good', 'Normal', 'Dormant'];

/* SMS metering — GSM-7 is 160 chars per part / 153 in concatenated mode.
   We assume GSM-7 for simplicity (matches the gateway default). */
const PART_SIZE = 160;
const partsFor = (len) => (len === 0 ? 0 : len <= PART_SIZE ? 1 : Math.ceil(len / PART_SIZE));

const fmtNum = (n) => new Intl.NumberFormat('en-US').format(n || 0);

/* ───────────────────────── small UI atoms ───────────────────────── */

function MultiSelectDropdown({ label, icon: Icon, options, value, onChange, allLabel = 'All' }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const allSelected = value.length === 0;
  const toggle = (v) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };

  // Build a human-readable summary for the trigger button.
  const summary = allSelected
    ? allLabel
    : (() => {
        const labels = value.map((v) => {
          const m = options.find((o) => (typeof o === 'string' ? o : o.value) === v);
          return typeof m === 'string' ? m : (m?.label ?? v);
        });
        if (labels.length === 1) return labels[0];
        return `${labels[0]} +${labels.length - 1}`;
      })();

  const idSlug = label.toLowerCase().replace(/\s+/g, '-');

  return (
    <div className="relative" ref={containerRef} data-testid={`messaging-filter-${idSlug}`}>
      <div className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold tracking-wider uppercase text-[#78716C]"
           style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        <Icon size={13} className="text-[#0A2540]" />
        {label}
      </div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`messaging-${idSlug}-trigger`}
        className="w-full h-10 px-3 rounded-lg border border-[#EDE6D8] bg-white text-sm text-[#1C1917] flex items-center justify-between gap-2 hover:border-[#0A2540] transition-colors"
      >
        <span className="truncate text-left">{summary}</span>
        <CaretDown size={13} className={`text-[#78716C] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 mt-1.5 z-30 bg-white border border-[#EDE6D8] rounded-lg shadow-lg max-h-72 overflow-auto"
             data-testid={`messaging-${idSlug}-menu`}>
          <button
            type="button"
            onClick={() => onChange([])}
            data-testid={`messaging-${idSlug}-all`}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm border-b border-[#FCFAF6] transition-colors ${
              allSelected ? 'bg-[#0A2540]/5 text-[#0A2540] font-semibold' : 'hover:bg-[#FCFAF6] text-[#1C1917]'
            }`}
          >
            <span className={`w-3.5 h-3.5 rounded border ${allSelected ? 'border-[#0A2540] bg-[#0A2540]' : 'border-[#D6D3D1]'} flex items-center justify-center`}>
              {allSelected && <Check size={10} weight="bold" className="text-white" />}
            </span>
            {allLabel}
          </button>
          {options.map((o) => {
            const v = typeof o === 'string' ? o : o.value;
            const labelText = typeof o === 'string' ? o : o.label;
            const active = value.includes(v);
            return (
              <button
                key={v}
                type="button"
                onClick={() => toggle(v)}
                data-testid={`messaging-${idSlug}-opt-${v}`}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                  active ? 'bg-[#0A2540]/5 text-[#0A2540] font-medium' : 'hover:bg-[#FCFAF6] text-[#1C1917]'
                }`}
              >
                <span className={`w-3.5 h-3.5 rounded border ${active ? 'border-[#0A2540] bg-[#0A2540]' : 'border-[#D6D3D1]'} flex items-center justify-center`}>
                  {active && <Check size={10} weight="bold" className="text-white" />}
                </span>
                <span className="truncate">{labelText}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, sub, accent = '#0A2540' }) {
  return (
    <div className="rounded-xl border border-[#EDE6D8] bg-white px-4 py-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-[#78716C]"
           style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-2xl font-bold tabular-nums" style={{ color: accent, fontFamily: 'JetBrains Mono, monospace' }}>
          {value}
        </span>
        {sub && <span className="text-[11px] text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{sub}</span>}
      </div>
    </div>
  );
}

/* ───────────────────────── main page ───────────────────────── */

export default function MessagingPage() {
  const { user } = useAuth();
  const authHeader = useMemo(() => {
    const token = (typeof window !== 'undefined' && localStorage.getItem('access_token')) || '';
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  }, []);

  const [campuses, setCampuses] = useState([]);
  const [categories, setCategories] = useState(CATEGORIES_FALLBACK);
  const [entityId, setEntityId] = useState('');         // super_admin only
  const [entities, setEntities] = useState([]);

  const [selCategories, setSelCategories] = useState([]);
  const [selCampusIds, setSelCampusIds] = useState([]);
  const [selStatuses, setSelStatuses] = useState([]);
  const [message, setMessage] = useState('');

  const [preview, setPreview] = useState({ recipients_total: 0, recipients_deliverable: 0, recipients_skipped_no_mobile: 0, sample: [] });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  /* Load filter options on mount + whenever the chosen entity changes. */
  useEffect(() => {
    const loadEntities = async () => {
      if (user?.role !== 'super_admin') return;
      try {
        const { data } = await axios.get(`${API_URL}/api/entities`, authHeader);
        setEntities(data || []);
        if (!entityId && data?.length) setEntityId(data[0].id);
      } catch (e) {
        toast.error('Could not load entities');
      }
    };
    loadEntities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const loadFilters = async () => {
      const params = entityId ? `?entity_id=${entityId}` : '';
      // Load both endpoints in parallel and handle each error independently
      // so one failure does not zero out the other dimension.
      const campusPromise = axios
        .get(`${API_URL}/api/campuses${params}`, authHeader)
        .then((r) => r.data || [])
        .catch((e) => {
          toast.error(`Could not load campuses (${e.response?.status || 'network'})`);
          return [];
        });
      const catPromise = axios
        .get(`${API_URL}/api/subscriber-categories`, authHeader)
        .then((r) => r.data || [])
        .catch(() => {
          // Silent fall back to a sensible default list so the UI never
          // shows an empty Categories dropdown.
          return CATEGORIES_FALLBACK.map((v) => ({ category: v }));
        });
      const [camps, cats] = await Promise.all([campusPromise, catPromise]);
      setCampuses(camps);
      // The /subscriber-categories endpoint returns rows of the shape
      //   { id, category, description, middleware_entity_id }
      // older code expected { value } / { label }. We tolerate strings AND
      // any of the known shape variations so the dropdown always populates.
      const normalised = (Array.isArray(cats) ? cats : []).map((c) => {
        if (typeof c === 'string') return c;
        return (c.category || c.value || c.label || c.name || '').trim();
      }).filter(Boolean);
      // De-dupe and sort for a tidy dropdown.
      const uniq = Array.from(new Set(normalised));
      setCategories(uniq.length ? uniq : CATEGORIES_FALLBACK);
    };
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  /* Debounced preview every time the filters change. */
  useEffect(() => {
    const handle = setTimeout(async () => {
      setLoadingPreview(true);
      try {
        const { data } = await axios.post(
          `${API_URL}/api/messaging/preview`,
          {
            entity_id: entityId || null,
            categories: selCategories,
            campus_ids: selCampusIds,
            statuses: selStatuses,
          },
          authHeader,
        );
        setPreview(data);
      } catch (e) {
        setPreview({ recipients_total: 0, recipients_deliverable: 0, recipients_skipped_no_mobile: 0, sample: [] });
      } finally {
        setLoadingPreview(false);
      }
    }, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, selCategories, selCampusIds, selStatuses]);

  /* Live computed values. */
  const charCount = message.length;
  const parts = partsFor(charCount);
  const totalMessages = parts * (preview.recipients_deliverable || 0);
  const remainingInPart = charCount === 0 ? PART_SIZE : (parts * PART_SIZE - charCount);

  const canSend = !sending && charCount > 0 && preview.recipients_deliverable > 0;

  const doSend = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/messaging/send`,
        {
          entity_id: entityId || null,
          categories: selCategories,
          campus_ids: selCampusIds,
          statuses: selStatuses,
          message,
        },
        authHeader,
      );
      setLastResult(data.broadcast);
      const sent = data.broadcast.recipients_sent || 0;
      const failed = data.broadcast.recipients_failed || 0;
      const blocked = data.broadcast.recipients_blocked || 0;
      if (blocked) {
        toast.warning(`Broadcast simulated — ${blocked} blocked (middleware writes disabled)`);
      } else if (failed) {
        toast.error(`${sent} sent · ${failed} failed`);
      } else {
        toast.success(`Broadcast sent to ${sent} recipients`);
      }
      setConfirmOpen(false);
      setMessage('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Broadcast failed');
    } finally {
      setSending(false);
    }
  };

  /* Campus options pass through {value,label} for the multi-select. */
  const campusOptions = useMemo(
    () => campuses.map((c) => ({ value: String(c.id), label: c.campus_name })),
    [campuses],
  );

  return (
    <div className="space-y-6 pb-8" data-testid="messaging-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-xl bg-[#0A2540] flex items-center justify-center">
              <ChatCircleText size={20} weight="fill" className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-[#0A2540]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Messaging Centre
              </h1>
              <p className="text-xs text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                BULK SMS · SEGMENTED BROADCASTS
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters card */}
      <div className="rounded-2xl border border-[#EDE6D8] bg-white p-6" data-testid="messaging-filters">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MultiSelectDropdown
            label="Categories"
            icon={Tag}
            options={categories}
            value={selCategories}
            onChange={setSelCategories}
            allLabel="All categories"
          />
          <MultiSelectDropdown
            label="Campuses"
            icon={GraduationCap}
            options={campusOptions}
            value={selCampusIds}
            onChange={setSelCampusIds}
            allLabel="All campuses"
          />
          <MultiSelectDropdown
            label="Performance status"
            icon={Pulse}
            options={STATUS_OPTIONS}
            value={selStatuses}
            onChange={setSelStatuses}
            allLabel="All statuses"
          />
        </div>
      </div>

      {/* Message body */}
      <div className="rounded-2xl border border-[#EDE6D8] bg-white p-6" data-testid="messaging-body">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-[#78716C]"
               style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            Message
          </div>
          <div className="flex items-center gap-3 text-[11px]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            <span className="text-[#78716C]">
              <span className="font-bold text-[#0A2540]">{charCount}</span> chars
            </span>
            <span className="text-[#78716C]">
              <span className="font-bold text-[#0A2540]">{parts}</span> {parts === 1 ? 'part' : 'parts'}
            </span>
            <span className={`${remainingInPart < 20 ? 'text-rose-600' : 'text-[#78716C]'}`}>
              {remainingInPart} left in part
            </span>
          </div>
        </div>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, 1000))}
          placeholder="Type your message here. 160 characters = 1 SMS part."
          rows={5}
          data-testid="messaging-message-input"
          className="resize-y"
        />
      </div>

      {/* Overview / Send strip */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        <StatTile label="Recipients · Match" value={loadingPreview ? '…' : fmtNum(preview.recipients_total)} sub="total" />
        <StatTile label="Deliverable" value={loadingPreview ? '…' : fmtNum(preview.recipients_deliverable)} sub="with mobile" accent="#0A2540" />
        <StatTile label="Skipped · No mobile" value={loadingPreview ? '…' : fmtNum(preview.recipients_skipped_no_mobile)} sub="filtered out" accent="#A16207" />
        <StatTile label="Total Messages" value={fmtNum(totalMessages)} sub={`${parts} × recipients`} accent="#059669" />
      </div>

      <div className="flex items-center justify-end">
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={!canSend}
          data-testid="messaging-send-btn"
          className="bg-[#0A2540] hover:bg-[#08203A] text-white px-6 h-10 rounded-full disabled:opacity-50"
        >
          <PaperPlaneTilt size={16} weight="fill" className="mr-2" />
          Send broadcast
        </Button>
      </div>

      {/* Sample recipients (collapsed list) */}
      {preview.sample?.length > 0 && (
        <div className="rounded-2xl border border-[#EDE6D8] bg-white p-5" data-testid="messaging-sample">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[#78716C]"
                 style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              Recipients · sample ({preview.sample.length} of {preview.recipients_deliverable})
            </div>
            <Users size={14} className="text-[#0A2540]" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-xs max-h-60 overflow-auto pr-2">
            {preview.sample.map((r) => (
              <div key={r.sub_id} className="flex items-center justify-between gap-3 py-1 border-b border-[#FCFAF6] last:border-0">
                <span className="truncate text-[#1C1917]">{r.sub_name}</span>
                <span className="font-mono text-[#78716C]">{r.mobile || '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Last broadcast result */}
      {lastResult && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5" data-testid="messaging-last-result">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700 mb-2"
               style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            Last broadcast — {lastResult.broadcast_id}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><div className="text-[10px] text-emerald-700">Sent</div><div className="font-bold text-emerald-700 tabular-nums">{fmtNum(lastResult.recipients_sent)}</div></div>
            <div><div className="text-[10px] text-rose-700">Failed</div><div className="font-bold text-rose-700 tabular-nums">{fmtNum(lastResult.recipients_failed)}</div></div>
            <div><div className="text-[10px] text-amber-700">Blocked</div><div className="font-bold text-amber-700 tabular-nums">{fmtNum(lastResult.recipients_blocked)}</div></div>
            <div><div className="text-[10px] text-[#78716C]">No mobile</div><div className="font-bold text-[#78716C] tabular-nums">{fmtNum(lastResult.skipped_no_mobile)}</div></div>
          </div>
        </div>
      )}

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-[560px]" data-testid="messaging-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm broadcast</DialogTitle>
            <DialogDescription>
              You are about to send this message to{' '}
              <span className="font-bold text-[#0A2540]">{fmtNum(preview.recipients_deliverable)}</span> recipients.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-[#FCFAF6] p-4 text-sm whitespace-pre-wrap border border-[#EDE6D8] max-h-48 overflow-auto">
            {message || <em className="text-[#78716C]">No message</em>}
          </div>
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div><div className="text-[10px] text-[#78716C] uppercase tracking-wider">Recipients</div><div className="font-bold text-[#0A2540] tabular-nums">{fmtNum(preview.recipients_deliverable)}</div></div>
            <div><div className="text-[10px] text-[#78716C] uppercase tracking-wider">Parts each</div><div className="font-bold text-[#0A2540] tabular-nums">{parts}</div></div>
            <div><div className="text-[10px] text-[#78716C] uppercase tracking-wider">Total SMS</div><div className="font-bold text-emerald-700 tabular-nums">{fmtNum(totalMessages)}</div></div>
          </div>
          {preview.recipients_skipped_no_mobile > 0 && (
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
              <WarningCircle size={14} className="shrink-0 mt-0.5" />
              <span>{fmtNum(preview.recipients_skipped_no_mobile)} matched subscribers have no mobile and will be skipped.</span>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={sending} data-testid="messaging-confirm-cancel">
              Cancel
            </Button>
            <Button
              onClick={doSend}
              disabled={sending}
              data-testid="messaging-confirm-send"
              className="bg-[#0A2540] hover:bg-[#08203A] text-white"
            >
              {sending ? (
                <><Spinner size={14} className="animate-spin mr-2" /> Sending…</>
              ) : (
                <><PaperPlaneTilt size={14} weight="fill" className="mr-2" /> Confirm & send</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
